import { Chip, Tooltip } from '@mui/material'
import {
  Description as ResumeIcon,
  LinkedIn as LinkedInIcon,
  Star as StarIcon,
  Assignment as AgreementIcon,
} from '@mui/icons-material'
import { Client } from '../../services/api'

// Deliverable/approval state, expressed through the MUI palette rather than the
// hand-mixed hex pairs this logic used to carry. Every value keeps its own word
// on the chip label, so state is never conveyed by colour alone.
type StatusTone = 'default' | 'info' | 'warning' | 'success'

const STATUS_TONE: Record<string, StatusTone> = {
  not_started: 'default',
  pending: 'warning',
  in_progress: 'info',
  in_review: 'warning',
  complete: 'success',
  not_needed: 'default',
}

function toneFor(statusKey: string): StatusTone {
  return STATUS_TONE[statusKey] ?? 'default'
}

// resumeStatus/linkedinStatus are derived in the backend from publish + approval
// state (MWA-10). In the deliverables stages the chip keeps the more human prose
// ("client approved" / "pending approval") that staff read there.
export function deriveResumeChip(
  status: string | undefined,
  stage: string | undefined
): { label: string; statusKey: string } {
  const s = status || 'not_started'
  if (stage === 'deliverables_in_progress' || stage === 'deliverables_complete') {
    switch (s) {
      case 'complete':
        return { label: 'client approved', statusKey: 'complete' }
      case 'in_review':
        return { label: 'pending approval', statusKey: 'pending' }
      case 'in_progress':
        return { label: 'in progress', statusKey: 'in_progress' }
      case 'not_started':
        return { label: 'not started', statusKey: 'not_started' }
    }
  }
  return { label: s.replace(/_/g, ' '), statusKey: s }
}

export function deriveLinkedinChip(status: string | undefined): { label: string; statusKey: string } {
  const s = status || 'not_started'
  return { label: s.replace(/_/g, ' '), statusKey: s }
}

function DeliverableChip({
  icon,
  label,
  statusKey,
  tooltip,
}: {
  icon: React.ReactElement
  label: string
  statusKey: string
  tooltip: string
}) {
  return (
    <Tooltip title={tooltip}>
      <Chip
        icon={icon}
        label={label}
        size="small"
        variant="outlined"
        color={toneFor(statusKey)}
        sx={{ textTransform: 'capitalize' }}
      />
    </Tooltip>
  )
}

/**
 * The deliverable-state chips for one client: resume and LinkedIn always, plus
 * the service agreement at onboarding and the North Star while it is being
 * approved. Which chips appear is deliberately stage-dependent — the same rule
 * the pipeline cards used before the table replaced them.
 */
export function ClientDeliverableChips({ client }: { client: Client }) {
  const resume = deriveResumeChip(client.resumeStatus, client.workflowStage)
  const linkedin = deriveLinkedinChip(client.linkedinStatus)

  const showServiceAgreement = client.workflowStage === 'onboarding'
  const showNorthStar = ['north_star', 'strategy_session'].includes(client.workflowStage)

  const saApproved = !!client.serviceAgreementApproved
  const nsLabel = client.northStarApproved
    ? 'NS approved'
    : client.northStarInternalApproved
      ? 'NS staff approved'
      : 'NS pending'
  const nsTooltip = client.northStarApproved
    ? 'North Star: approved by client'
    : client.northStarInternalApproved
      ? 'North Star: internally approved by staff'
      : 'North Star: pending approval'
  const nsStatusKey = client.northStarApproved
    ? 'complete'
    : client.northStarInternalApproved
      ? 'in_review'
      : 'not_started'

  return (
    <>
      {showServiceAgreement && (
        <DeliverableChip
          icon={<AgreementIcon sx={{ fontSize: 14 }} />}
          label={saApproved ? 'SA approved' : 'SA pending'}
          statusKey={saApproved ? 'complete' : 'not_started'}
          tooltip={
            saApproved
              ? 'Service agreement: approved by client'
              : 'Service agreement: awaiting client approval'
          }
        />
      )}
      <DeliverableChip
        icon={<ResumeIcon sx={{ fontSize: 14 }} />}
        label={resume.label}
        statusKey={resume.statusKey}
        tooltip={`Resume: ${resume.label}`}
      />
      <DeliverableChip
        icon={<LinkedInIcon sx={{ fontSize: 14 }} />}
        label={linkedin.label}
        statusKey={linkedin.statusKey}
        tooltip={`LinkedIn: ${linkedin.label}`}
      />
      {showNorthStar && (
        <DeliverableChip
          icon={<StarIcon sx={{ fontSize: 14 }} />}
          label={nsLabel}
          statusKey={nsStatusKey}
          tooltip={nsTooltip}
        />
      )}
    </>
  )
}

/** Whole days since the client's record last changed. */
export function getDaysInStage(updatedAt: string): number {
  const updated = new Date(updatedAt)
  if (Number.isNaN(updated.getTime())) return 0
  const diff = Math.abs(Date.now() - updated.getTime())
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

/** A client sitting in one stage longer than this reads as stalled. */
export const AGING_DAYS = 3

/** True when nobody is assigned to this client in any team role. */
export function needsTeamAssignment(client: Client): boolean {
  return !client.clientSuccess && !client.resumeWriter && !client.coach
}
